import React, { useMemo } from "react";
import styled from "styled-components";

import { COLLAB_RUBRIC_URL } from "constants/rubricLinks";
import { colors } from "utils";

import { EM_DASH, nextSortState } from "./studentReportTable";
import {
  PERIOD_SORT_KEYS,
  buildPeriods,
  displayedPeriods,
  periodNoun,
  periodReportUrl,
  weekMinutesText,
} from "./trendPeriods";
import { CollaborationTrendChart } from "./CollaborationTrendChart";
import { RubricLink } from "./RubricLink";
import {
  BodyRow,
  Cell,
  HeaderButton,
  LevelTag,
  SortGlyph,
  SortableHeader,
  Table,
  TableWrapper,
} from "./reportTableStyles";

// The Activity & Collaboration Trend tab (OKO2-3038), node 2639:18134.
//
// The chart and the table describe THE SAME PERIODS from THE SAME ROWS. The
// chart keeps chronological order whatever the table is sorted by — a trend
// re-ordered by minutes is not a trend — and the table sorts freely, because a
// teacher scanning for the worst period is not reading a trend at all.

export const ActivityCollaborationTrend = ({
  rows = [],
  studentId = null,
  classroomId = null,
  downloadSlot = null,
  sort = null,
  onSortChange = () => {},
}) => {
  const { grouping, periods } = useMemo(() => buildPeriods(rows), [rows]);
  const displayed = useMemo(() => displayedPeriods(rows, sort), [rows, sort]);

  // A period row opens the sessions it is counting, in a new tab, matching what
  // a Session Detail row click already does.
  const openPeriod = (period) => {
    if (!period.sessionCount) return;
    window.open(
      periodReportUrl({ start: period.start, studentId, classroomId }),
      "_blank",
      "noopener,noreferrer"
    );
  };

  const sortHeader = (column, label, id) => (
    <SortableHeader scope="col" id={id}>
      <HeaderButton
        type="button"
        $centered
        onClick={() => onSortChange(nextSortState(sort, column))}
      >
        <span>{label}</span>
        <SortGlyph direction={sort?.column === column ? sort.direction : null} />
      </HeaderButton>
    </SortableHeader>
  );

  return (
    <TrendColumn>
      <DescriptionRow data-testid="dc-toolbar">
        <Description>
          <strong>Building Better Collaboration:</strong> At the end of each question, OKO uses the{" "}
          <RubricLink href={COLLAB_RUBRIC_URL} variant="inline">
            Collaboration Rubric
          </RubricLink>
          <br />
          to provide the group with a Teamwork Score and immediate, actionable feedback to improve
          their math discourse. Teacher Reports go further, giving a Collaboration score for each
          student.
        </Description>
        {downloadSlot}
      </DescriptionRow>

      <CollaborationTrendChart periods={periods} grouping={grouping} />

      <TableWrapper>
        <PeriodTable>
          {/* The design gives each of the four cells `flex-[1 0 0]` — equal
              quarters, not the natural widths an auto-laid-out table picks.
              Without this the first column takes the space the other three
              want and the header stops lining up with the chart above it. */}
          <colgroup>
            <col span="4" width="25%" />
          </colgroup>
          <thead>
            <tr>
              {/* The column is named after what a row IS, so a table of
                  months is not headed "Week". */}
              {sortHeader(PERIOD_SORT_KEYS.PERIOD, periodNoun(grouping), "col-period")}
              {sortHeader(PERIOD_SORT_KEYS.SESSION_COUNT, "Session Count", "col-period-sessions")}
              {sortHeader(PERIOD_SORT_KEYS.TOTAL_TIME, "Total Session Time", "col-period-time")}
              {sortHeader(
                PERIOD_SORT_KEYS.AVERAGE_LEVEL,
                "Average Collaboration Score",
                "col-period-level"
              )}
            </tr>
          </thead>
          <tbody>
            {displayed.map((period) => (
              // A padded empty period is not a link to a window with nothing in it.
              <BodyRow
                key={period.key}
                data-testid="trend-period-row"
                $clickable={period.sessionCount > 0}
                onClick={() => openPeriod(period)}
              >
                <Cell headers="col-period">{period.label}</Cell>
                <Cell headers="col-period-sessions">{period.sessionCount}</Cell>
                <Cell headers="col-period-time">{weekMinutesText(period.totalMinutes)}</Cell>
                <Cell headers="col-period-level">
                  {period.level === null ? EM_DASH : <LevelTag level={period.level} noStar />}
                </Cell>
              </BodyRow>
            ))}
          </tbody>
        </PeriodTable>
      </TableWrapper>
    </TrendColumn>
  );
};

// The description row, the chart with its key, and the table, 24px apart.
const TrendColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

// The rubric paragraph, and the Download menu on its right at its full size.
const DescriptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;

  & > :last-child {
    flex-shrink: 0;
  }
`;

// Body/Large in the design: Inter 18/26, the lead-in in bold.
const Description = styled.p`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};

  strong {
    font-weight: 700;
  }
`;

// Every row the height of a row that carries a level tag -- 8 + 30 + 8, plus
// the 1px rule CSS draws outside the cell where Figma draws it inside -- so a
// period with no score ("—") does not collapse to a shorter row. The frame's
// rows are uniform (4485:72869 onward, 46px each).
const PeriodTable = styled(Table)`
  table-layout: fixed;

  /* Every cell centred, as the design's table notes ask; rows on the frame's
     off-white. */
  tbody tr {
    background-color: #fffefe;
  }

  tbody td {
    height: 47px;
    box-sizing: border-box;
    text-align: center;
  }
`;
